import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings } from 'lucide-react';

interface Assumptions {
  horizon_months: number;
  gross_target_pct: number;
  net_target_pct: number;
  activation_cost_per_acct: number;
  product_type: string;
  recency_half_life_months: number;
  volatility_pct: number;
}

interface AssumptionsFormProps {
  initialValues?: Partial<Assumptions>;
  onSubmit: (assumptions: Assumptions) => void;
  loading?: boolean;
}

export const AssumptionsForm = ({ 
  initialValues = {}, 
  onSubmit, 
  loading = false 
}: AssumptionsFormProps) => {
  const [assumptions, setAssumptions] = useState<Assumptions>({
    horizon_months: initialValues.horizon_months || 36,
    gross_target_pct: initialValues.gross_target_pct || 36.5,
    net_target_pct: initialValues.net_target_pct || 12.5,
    activation_cost_per_acct: initialValues.activation_cost_per_acct || 50,
    product_type: initialValues.product_type || 'generic',
    recency_half_life_months: initialValues.recency_half_life_months || 24,
    volatility_pct: initialValues.volatility_pct || 15.0
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(assumptions);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Valuation Assumptions
        </CardTitle>
        <CardDescription>
          Configure parameters for portfolio valuation
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="product_type">Product Type</Label>
              <Select
                value={assumptions.product_type}
                onValueChange={(value) => 
                  setAssumptions({ ...assumptions, product_type: value })
                }
              >
                <SelectTrigger id="product_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="generic">Generic</SelectItem>
                  <SelectItem value="microloan">Microloan</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="hp">Hire Purchase</SelectItem>
                  <SelectItem value="payday">Payday</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="horizon_months">Horizon (Months)</Label>
              <Input
                id="horizon_months"
                type="number"
                min="1"
                max="60"
                value={assumptions.horizon_months}
                onChange={(e) =>
                  setAssumptions({ ...assumptions, horizon_months: parseInt(e.target.value) })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gross_target_pct">Gross Target %</Label>
              <Input
                id="gross_target_pct"
                type="number"
                step="0.1"
                value={assumptions.gross_target_pct}
                onChange={(e) =>
                  setAssumptions({ ...assumptions, gross_target_pct: parseFloat(e.target.value) })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="net_target_pct">Net Target %</Label>
              <Input
                id="net_target_pct"
                type="number"
                step="0.1"
                value={assumptions.net_target_pct}
                onChange={(e) =>
                  setAssumptions({ ...assumptions, net_target_pct: parseFloat(e.target.value) })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="activation_cost">Activation Cost per Account</Label>
              <Input
                id="activation_cost"
                type="number"
                min="0"
                value={assumptions.activation_cost_per_acct}
                onChange={(e) =>
                  setAssumptions({ ...assumptions, activation_cost_per_acct: parseInt(e.target.value) })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recency_half_life">Recency Half-life (Months)</Label>
              <Input
                id="recency_half_life"
                type="number"
                min="1"
                max="120"
                value={assumptions.recency_half_life_months}
                onChange={(e) =>
                  setAssumptions({ ...assumptions, recency_half_life_months: parseInt(e.target.value) })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="volatility_pct">Volatility %</Label>
              <Input
                id="volatility_pct"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={assumptions.volatility_pct}
                onChange={(e) =>
                  setAssumptions({ ...assumptions, volatility_pct: parseFloat(e.target.value) })
                }
              />
            </div>
          </div>

          <div className="pt-4">
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Running Valuation...' : 'Run Valuation'}
            </Button>
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              <strong>Recency Half-life:</strong> Time period over which payment probability decays to 50%
            </p>
            <p>
              <strong>Volatility:</strong> ±% variation used for confidence interval calculation
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
